import React from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  Fade
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseButton from './Common/CloseButton';

// Local theme copy so component is self-contained
const themeColors = {
  primary: '#5757b0',
  primaryLight: '#7878c5',
  primaryDark: '#3a3a8c',
  accent: '#ff8a65',
  text: '#333',
  lightText: '#f5f5f5',
  background: '#f8f9fa',
  cardBackground: '#ffffff'
};

const GenericConnectorIngestions = ({
  open,
  onClose,
  ingestionFilter,
  setIngestionFilter,
  ingestionsLoading,
  ingestionsError,
  ingestionItems,
  filteredIngestionItems,
  getIngestionSource,
  getIngestionTitle,
  formatIngestionTimestamp,
  getIngestionStatus,
  getIngestionStatusLabel,
  getIngestionStatusColor
}) => {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 300 }}
      PaperProps={{
        elevation: 6,
        sx: {
          borderRadius: 2,
          background: `linear-gradient(to right bottom, ${themeColors.cardBackground}, ${themeColors.background})`,
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${themeColors.primary} 0%, ${themeColors.primaryDark} 100%)`,
          color: themeColors.lightText,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pb: 3
        }}
      >
        {t('generic_connector.last_ingestions_title') || "Last ingestions"}
        <CloseButton
          ariaLabel="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: themeColors.lightText
          }}
        />
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, mt: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>{t('generic_connector.ingestions_filter_label') || "Filter"}</InputLabel>
            <Select
              value={ingestionFilter}
              label={t('generic_connector.ingestions_filter_label') || "Filter"}
              onChange={(event) => setIngestionFilter(event.target.value)}
              disabled={ingestionsLoading}
              MenuProps={{ PaperProps: { sx: { zIndex: 1500 } } }}
            >
              <MenuItem value="all">{t('generic_connector.ingestions_filter_all') || "All"}</MenuItem>
              <MenuItem value="success">{t('generic_connector.ingestions_filter_success') || "Ingested only"}</MenuItem>
              <MenuItem value="skipped">{t('generic_connector.ingestions_filter_skipped') || "Skipped only"}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {ingestionsLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">
              {t('generic_connector.last_ingestions_loading') || "Loading ingestions..."}
            </Typography>
          </Box>
        )}

        {ingestionsError && (
          <Typography color="error" variant="body2" sx={{ py: 1 }}>
            {ingestionsError || t('generic_connector.last_ingestions_error') || "Unable to load ingestions."}
          </Typography>
        )}

        {!ingestionsLoading && !ingestionsError && ingestionItems.length === 0 && (
          <Typography color="text.secondary" variant="body2" sx={{ py: 1 }}>
            {t('generic_connector.last_ingestions_empty') || "No ingestions yet."}
          </Typography>
        )}

        {!ingestionsLoading && !ingestionsError && ingestionItems.length > 0 && filteredIngestionItems.length === 0 && (
          <Typography color="text.secondary" variant="body2" sx={{ py: 1 }}>
            {t('generic_connector.last_ingestions_filtered_empty') || "No ingestions match this filter."}
          </Typography>
        )}

        {!ingestionsLoading && !ingestionsError && filteredIngestionItems.length > 0 && (
          <List sx={{ mt: 1 }}>
            {filteredIngestionItems.map((item, index) => {
              const source = getIngestionSource(item);
              const titleText = getIngestionTitle(item);
              const eventTime = formatIngestionTimestamp(item?.eventtime);
              const ingestedAt = formatIngestionTimestamp(item?.ingested_at);
              const statusValue = getIngestionStatus(item);
              const statusLabel = getIngestionStatusLabel(statusValue);
              const statusColor = getIngestionStatusColor(statusValue);
              const statusVariant = statusValue === 'skipped' ? 'outlined' : 'filled';
              const key = item?.timeevent_id || `${item?.ingested_at || 'ingest'}-${index}`;

              return (
                <React.Fragment key={key}>
                  <ListItem alignItems="flex-start" disableGutters>
                    <ListItemText
                      primary={titleText}
                      primaryTypographyProps={{ fontWeight: 600 }}
                      secondary={(
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.5 }}>
                          {statusLabel && (
                            <Chip
                              size="small"
                              label={statusLabel}
                              color={statusColor}
                              variant={statusVariant}
                              sx={{ alignSelf: 'flex-start' }}
                            />
                          )}
                          {source && (
                            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                              {t('generic_connector.ingestions_source') || "Source"}: {source}
                            </Typography>
                          )}
                          {eventTime && (
                            <Typography variant="caption" color="text.secondary">
                              {t('generic_connector.ingestions_event_time') || "Event time"}: {eventTime}
                            </Typography>
                          )}
                          {ingestedAt && (
                            <Typography variant="caption" color="text.secondary">
                              {t('generic_connector.ingestions_ingested_at') || "Ingested"}: {ingestedAt}
                            </Typography>
                          )}
                        </Box>
                      )}
                    />
                  </ListItem>
                  {index < filteredIngestionItems.length - 1 && <Divider component="li" />}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GenericConnectorIngestions;
